import React from "react";
import {
  CurrencyRupeeIcon,
  UserGroupIcon,
  ClockIcon,
  TrendingUpIcon,
  CheckCircleIcon,
  ExclamationIcon,
} from "@heroicons/react/outline";

const DashboardWidgets = ({ stats, pendingSummary, loading }) => {
  // Calculate urgent payment count (7+ days)
  const urgentCount = pendingSummary?.aging?.["7d+"]?.length || 0;
  const totalPending = pendingSummary?.total || 0;

  const widgets = [
    {
      title: "Active Plans",
      value: stats.activePlans || 0,
      change: stats.newPlans || 0,
      changeLabel: "new today",
      icon: UserGroupIcon,
      color: "bg-blue-500",
    },
    {
      title: `${stats.period || "Month"}'s Collection`,
      value: `₹${(stats.totalCollections || 0).toLocaleString()}`,
      change: null,
      icon: CurrencyRupeeIcon,
      color: "bg-green-500",
    },
    {
      title: "Pending Verifications",
      value: totalPending || 0,
      subtitle: urgentCount > 0 ? `${urgentCount} urgent (7+ days)` : null,
      urgent: urgentCount > 0,
      change:
        pendingSummary?.totalAmount > 0
          ? `₹${pendingSummary.totalAmount.toLocaleString()}`
          : null,
      changeLabel: "pending amount",
      icon: urgentCount > 0 ? ExclamationIcon : CheckCircleIcon,
      color: urgentCount > 0 ? "bg-red-500" : "bg-yellow-500",
    },
    {
      title: "Upcoming Maturities",
      value: stats.upcomingMaturities || 0,
      changeLabel: "this month",
      icon: TrendingUpIcon,
      color: "bg-purple-500",
    },
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white rounded-lg shadow p-6 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
            <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {widgets.map((widget, index) => (
        <div
          key={index}
          className={`bg-white rounded-lg shadow p-6 ${
            widget.urgent ? "ring-2 ring-red-300" : ""
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">
                {widget.title}
              </p>
              <p className="mt-2 text-3xl font-semibold text-gray-900">
                {widget.value}
              </p>

              {/* Subtitle for urgent notifications */}
              {widget.subtitle && (
                <p className="mt-1 text-xs font-medium text-red-600">
                  {widget.subtitle}
                </p>
              )}

              {/* Change/Additional Info */}
              {widget.change !== null && (
                <p className="mt-2 text-sm text-gray-600">
                  <span className="font-medium text-green-600">
                    {widget.change}
                  </span>{" "}
                  {widget.changeLabel}
                </p>
              )}

              {/* Urgent Flag */}
              {widget.urgent && !widget.subtitle && (
                <p className="mt-2 text-sm font-medium text-red-600">
                  Requires immediate attention
                </p>
              )}
            </div>
            <div className={`${widget.color} rounded-full p-3`}>
              <widget.icon className="h-6 w-6 text-white" />
            </div>
          </div>

          {/* Progress Bar for Pending Verifications */}
          {index === 2 && totalPending > 0 && (
            <div className="mt-4">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>SLA: 24 hours</span>
                <span>
                  {urgentCount > 0
                    ? `${((urgentCount / totalPending) * 100).toFixed(
                        0
                      )}% overdue`
                    : "On track"}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    urgentCount > 0 ? "bg-red-500" : "bg-green-500"
                  }`}
                  style={{
                    width: `${Math.min(
                      ((totalPending - urgentCount) / totalPending) * 100,
                      100
                    )}%`,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default DashboardWidgets;
